import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Spin } from "antd";
import dayjs from "dayjs";
import api from "../../services/api";

const BillPrint: React.FC = () => {
  const { id } = useParams();
  const [bill, setBill] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setLoadError(null);
    api
      .get(`/billing/${id}`)
      .then((res) => setBill(res.data.data))
      .catch(() => setLoadError("Failed to load bill"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Spin style={{ margin: 40 }} />;
  if (loadError || !bill) {
    return (
      <Alert
        type="error"
        showIcon
        message={loadError || "Bill not found"}
        style={{ margin: 40, maxWidth: 480 }}
      />
    );
  }

  const snap = bill.pharmacySnapshot || {};

  return (
    <div
      style={{
        padding: 24,
        maxWidth: 800,
        margin: "0 auto",
        background: "#fff",
      }}
    >
      <div style={{ textAlign: "right", marginBottom: 16 }}>
        <Button type="primary" onClick={() => window.print()}>
          Print / Save PDF
        </Button>
      </div>
      <div className="print-area">
        <h2 style={{ marginBottom: 4 }}>{snap.pharmacyName || "Pharmacy"}</h2>
        <div style={{ fontSize: 12, color: "#555" }}>
          {snap.address}
          {snap.phone ? ` | Ph: ${snap.phone}` : ""}
          <br />
          {snap.gstin ? `GSTIN: ${snap.gstin}` : ""}
          {snap.drugLicenseNumber ? ` | DL: ${snap.drugLicenseNumber}` : ""}
        </div>
        <hr />
        <h3>Tax Invoice</h3>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 13,
          }}
        >
          <div>
            <div>
              <strong>Bill No:</strong> {bill.billNumber}
            </div>
            <div>
              <strong>Date:</strong>{" "}
              {dayjs(bill.billDate).format("DD/MM/YYYY HH:mm")}
            </div>
            <div>
              <strong>Customer:</strong>{" "}
              {bill.patient?.name || bill.customer?.name || "Walk-in Customer"}
              {(bill.patient?.phone || bill.customer?.phone) &&
                ` (${bill.patient?.phone || bill.customer?.phone})`}
            </div>
            {bill.prescriptionNumber && (
              <div>
                <strong>Rx No:</strong> {bill.prescriptionNumber}
              </div>
            )}
            {bill.doctorName && (
              <div>
                <strong>Doctor:</strong> {bill.doctorName}
              </div>
            )}
          </div>
          <div>
            <div>
              <strong>Payment:</strong> {bill.paymentMethod} /{" "}
              {bill.paymentStatus}
            </div>
          </div>
        </div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: 16,
            fontSize: 12,
          }}
        >
          <thead>
            <tr>
              {[
                "#",
                "Medicine",
                "HSN",
                "Batch",
                "Exp",
                "Qty",
                "Rate",
                "Disc%",
                "Amount",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    borderBottom: "1px solid #333",
                    textAlign: "left",
                    padding: 4,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bill.items.map((item: any, idx: number) => (
              <tr key={idx}>
                <td style={{ padding: 4 }}>{idx + 1}</td>
                <td style={{ padding: 4 }}>
                  {item.name || item.medicine?.name}
                </td>
                <td style={{ padding: 4 }}>{item.hsnCode}</td>
                <td style={{ padding: 4 }}>{item.batchNumber}</td>
                <td style={{ padding: 4 }}>
                  {item.expiryDate
                    ? dayjs(item.expiryDate).format("MM/YY")
                    : ""}
                </td>
                <td style={{ padding: 4 }}>{item.quantity}</td>
                <td style={{ padding: 4 }}>{item.unitPrice?.toFixed(2)}</td>
                <td style={{ padding: 4 }}>{item.discount || 0}</td>
                <td style={{ padding: 4 }}>{item.total?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 16, textAlign: "right", fontSize: 13 }}>
          <div>Subtotal: ₹{bill.subtotal?.toFixed(2)}</div>
          {(bill.billDiscount || 0) > 0 && (
            <div>Bill Discount: −₹{Number(bill.billDiscount).toFixed(2)}</div>
          )}
          {(bill.roundOff || 0) !== 0 && (
            <div>Round Off: ₹{Number(bill.roundOff).toFixed(2)}</div>
          )}
          <div>
            <strong>Grand Total: ₹{bill.grandTotal?.toFixed(2)}</strong>
          </div>
          <div>Paid: ₹{bill.amountPaid?.toFixed(2)}</div>
        </div>
      </div>
      <style>{`
        @media print {
          button { display: none !important; }
          body { margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default BillPrint;
